import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { CustomerContext } from '../../../contexts/CustomerContext';
import Pose from '../../../models/Pose';
import { OrderPackage } from '../../../models';
import Sheet from '../../../models/Sheet';
import Spinner from '../../Spinner';
import Image from '../../Image';
import Button from '../../Button';
import usePackageOptions from '../../../hooks/usePackageOptions';
import useBackgroundsFromPackage from '../package-builder/use-backgrounds-from-package';
import offeredOptions, { priceOf } from '../../PackageChooser/offeredOptions';
import discountedPrice, { money } from '../../../utils/offerPricing';
import useOfferCoupon from '../../../hooks/useOfferCoupon';
import useApplyOfferToCart from '../../../hooks/useApplyOfferToCart';
import PromoBanner from '../../PromoBanner';
import { routes } from '../../../routes';
import useQueryString from '../../../hooks/use-query-string';
import styles from './SportsTeamPicture.module.sass';
import spinnerClasses from '../Sittings/Sittings.module.sass';

/**
 * The sport player who was never photographed on their own.
 *
 * 87 of the 134 players in the first sportsfall2027 run had no individual
 * picture -- 76 of them hold exactly the team shot and its derived collage.
 * The favorites gallery asks them to choose a favourite out of one landscape
 * group photo, which is not a choice, and the package grid offers them a wall
 * of packages they cannot fill.
 *
 * So this is a LANDING PAGE for the one thing they can actually buy. Same
 * package, same price, same order package -- "Team Picture Only" off their own
 * sport pricelist -- presented as a single photograph with a price and a
 * button instead of a one-card grid under two tabs.
 *
 * WHO ARRIVES is decided by PackageSelection on
 * StudentImage#sport_team_photo_only?, the one implementation of that rule.
 * This page verifies only that it was handed a pose and that the pricelist
 * really has a team option; either missing, it falls back to the gallery
 * rather than render a frame with nothing in it.
 */

// Which option this page sells: the pricelist's team picture.
//
// `offeredOptions` first, so an add-on (anything with a `code`), the buy-all
// upsell and anything this school never priced are gone before we look at a
// name -- selling an unpriced option is a dead end, and an add-on sold here
// would be sold twice.
//
// Then the NAME, and it must say team PICTURE or team PHOTO -- never a bare
// "team".
//
// ⚠️ "TEAM" ALONE IS A TIER NAME. Package 40 "Youth Sports" (season `sports`,
// so it is a sport pricelist) has no team picture at all; its only "team"
// option is 209 "E- Team Player", a $30 tier of (1) Memory Mate, (1) 5x7,
// (2) 3x5 and (8) Wallets. A bare net picked it, and buildSheets would have
// filled all twelve prints with the same landscape group shot -- including a
// Memory Mate, which is cut for two different poses. "D: Team Player" and
// "F: Team Poster" are the same trap on other pricelists.
//
// Both spellings are real: every current sport pricelist says "Team Picture
// Only" (649, 656, 660, 663, 664, 668, 669) and older ones say "Team Photo".
//
// It must also actually CONTAIN something. Options 248 and 1295 are named
// "Team Photo" and carry no image types at all; buildSheets returns [] for
// those, and the customer would buy a priced line with no print on it and no
// pose linked to it. Not a single print, though -- 656's "Team Picture Only"
// is legitimately two 8x10s.
//
// Cheapest breaks a remaining tie: this page exists for the player who can buy
// one thing, and that should be the least we can sell them, not the most.
//
// Null means this pricelist has no team option -- see the redirect below.
export const teamPictureOption = (options) => {
  const candidates = offeredOptions(options).filter((option) => {
    const name = (option.name || '').toLowerCase();
    const sellsAPrint = (option.imageTypes || []).length > 0;

    return sellsAPrint && (name.includes('team picture') || name.includes('team photo'));
  });

  if (candidates.length === 0) return null;

  return candidates.reduce((best, option) => (
    (priceOf(option) ?? Infinity) < (priceOf(best) ?? Infinity) ? option : best
  ));
};

// One Sheet per PRINT, not per option: an image type carries a count ("(4) 5x7"
// is one image type, four sheets). Lifted from PastImagesQuickOrder's
// buildSheetsForOption so there is one shape of this in the app.
//
// The background is the pricelist's default and the print pipeline ignores it
// here: a team picture is a Phase 1.5 JPG that already has its own background,
// and only a transparent PNG gets one composited behind it.
export const buildSheets = (option, pose, backgroundId) => (option.imageTypes || [])
  .map((imageType) => new Array(imageType.count || 1).fill(imageType))
  .reduce((acc, arr) => acc.concat(arr), [])
  .map((imageType, index) => new Sheet({
    index,
    poseId: pose.id,
    pose,
    imageTypeId: imageType.id,
    imageType,
    backgroundId: imageType.requiresBg === false ? null : backgroundId,
  }));

const SportsTeamPicture = () => {
  const { packageId } = useParams();
  const { favorites } = useQueryString();
  const history = useHistory();
  const { customer, setCustomer } = useContext(CustomerContext);
  const { currentStudent, currentSchool } = customer;

  const poseId = [favorites].flat().filter(Boolean)[0];
  const studentId = currentStudent && currentStudent.id;
  const [pose, setPose] = useState(null);
  const [poseUnavailable, setPoseUnavailable] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // isFetched, NOT isLoading. usePackageOptions sets placeholderData: [], and
  // react-query reports a placeholder as status 'success' with isLoading false
  // -- so isLoading says "settled" on the very first render, before the request
  // has been made. isFetched turns true only once a real response has landed.
  const { data: options, isFetched: optionsFetched } = usePackageOptions(
    packageId, currentSchool && currentSchool.id,
  );
  const backgrounds = useBackgroundsFromPackage(packageId);

  // The promotional offer for THIS pricelist -- the same time-windowed
  // for_landing_page engine the package grid uses, so a player sent here sees
  // the offer a player sent to the grid would have. studentId is the external
  // school-scoped id (Student#studentId), which the resolver looks up.
  const { data: offer } = useOfferCoupon(
    currentStudent && currentStudent.studentId, currentSchool && currentSchool.id, packageId,
  );
  // Applied because the offer is SHOWN here (banner + struck price). The grid's
  // rule, and the reason for it: auto-applying where the promo UI does not
  // render is a silent, unexplained discount at checkout.
  useApplyOfferToCart(offer);

  useEffect(() => {
    if (!poseId) {
      setPoseUnavailable(true);
      return;
    }

    // ⚠️ THE POSE ID IS A URL PARAMETER AND Api::PosesController#show IS NOT
    // FILTERED -- only `index` is. So Pose.find resolves ANY pose in the system,
    // and this page must check it is this student's before framing it. The
    // builder it replaces already does (it intersects `favorites` with the
    // student's own poses).
    //
    // It is not only tampering: a parent who used "Add another student" and then
    // came Back to a cached team-picture URL would see the FIRST child's photo
    // under the SECOND child's name -- and on Add to Cart,
    // OrderPackage#align_student_with_pictures! silently rewrites the order
    // package's student_id AND school_id to the pose's owner.
    Pose
      .includes('student_image')
      .find(poseId)
      .then((response) => {
        const found = response.data;
        const ownerId = found && found.studentImage && found.studentImage.studentId;

        if (!ownerId || String(ownerId) !== String(studentId)) {
          setPoseUnavailable(true);
          return;
        }

        setPose(found);
      })
      .catch(() => setPoseUnavailable(true));
  }, [poseId, studentId]);

  const option = teamPictureOption(options);

  const addToCart = () => {
    // backgrounds is null until its request lands; firing before then writes
    // every Sheet with a null background_id. The builder blocks its whole render
    // on the same condition.
    if (!customer.cart || !option || !pose || !backgrounds || isAdding) return;

    setIsAdding(true);

    const orderPackage = new OrderPackage({
      cartId: customer.cart.id,
      studentId: currentStudent.id,
      optionId: option.id,
      packageId,
      apiOption: option,
      extraPoses: 0,
      sheets: buildSheets(option, pose, (backgrounds && backgrounds[0] && backgrounds[0].id) || null),
      isPreviewHidden: false,
    });

    // Do NOT set studentImageId / studentImage: a populated studentImage makes
    // orderPackage.studentImage.orderPackages[0] === orderPackage, and /c/review
    // stringifies the cart. The linkage flows through each Sheet's pose.
    // ⚠️ spraypaint RESOLVES FALSE on a 422, it does not reject -- see
    // model.js#_handleResponse. A `.catch` alone would have walked the customer
    // to /c/review with nothing in their cart, no message, and the button stuck
    // on "Adding…". OrderPackage validates price and Sheet requires an image
    // type, so a 422 here is reachable. Same check the package builder and
    // PastImagesQuickOrder make.
    orderPackage
      .save({ with: 'sheets' })
      .then((saved) => {
        if (!saved) {
          setIsAdding(false);
          toast('There was an error adding this to your cart. Please refresh and try again.', { type: 'error' });
          return;
        }

        setCustomer({ ...customer, currentOrderPackage: orderPackage });
        history.push(routes.review());
      })
      .catch(() => {
        setIsAdding(false);
        toast('There was an error adding this to your cart. Please refresh and try again.', { type: 'error' });
      });
  };

  // Nothing to feature, or nothing to sell. The favorites gallery lists
  // whatever the player does have, so it stays the honest destination rather
  // than a landing page built around a photo or a price we do not have.
  if (poseUnavailable) {
    return <Redirect to={routes.elementaryMultiPoseBuilder(packageId)} />;
  }

  // usePackageOptions is `enabled: !!(packageId && schoolId)`, so with no school
  // the query never runs and isFetched never turns true. Spinning forever is
  // not an answer; the gallery is.
  if (!currentSchool || !currentSchool.id) {
    return <Redirect to={routes.elementaryMultiPoseBuilder(packageId)} />;
  }

  // Fetched and no team option on this pricelist -- including a pricelist with
  // no options at all, which the earlier `options.length > 0` guard left
  // spinning forever.
  if (optionsFetched && !option) {
    return <Redirect to={routes.elementaryMultiPoseBuilder(packageId)} />;
  }

  if (!pose || !optionsFetched) {
    return (
      <div className={spinnerClasses.spinnerContainer}>
        <Spinner color="blue" />
      </div>
    );
  }

  const firstName = currentStudent && currentStudent.firstName;
  const rawPrice = priceOf(option);
  // A "now" price for PERCENTAGE offers only -- a flat-rate credit is taken off
  // the order at checkout, not off this line, so striking the price for one
  // would promise a discount twice.
  const discounted = discountedPrice(rawPrice, offer);
  // The admin-authored lines the package cards show, in the same order: the
  // tagline under the name (where a pricelist puts "FREE Shipping! Code:
  // FREESHIP25"), then the description as the package contents. Falls back to
  // the image-type names only when a pricelist left the description blank.
  const imageTypeNames = (option.imageTypes || []).map((imageType) => imageType.name).filter(Boolean);
  const contents = option.description ? [option.description] : imageTypeNames;

  return (
    <div className={styles.page}>
      <ToastContainer position="bottom-left" />

      {/* States the offer once, with the coupon CODE -- the struck price below
          shows the discount but never the code. */}
      <PromoBanner offer={offer} />

      <h1 className={styles.title}>
        {firstName ? `${firstName}'s Team Picture` : 'Your Team Picture'}
      </h1>

      <div className={styles.photoFrame}>
        <Image src={pose.watermark()} alt="Team picture" classes={styles.photo} />
      </div>

      <div className={styles.details}>
        <p className={styles.optionName}>{option.name}</p>
        {option.tagline && <p className={styles.tagline}>{option.tagline}</p>}
        {contents.length > 0 && (
          <ul className={styles.contents}>
            {contents.map((line) => <li key={line}>{line}</li>)}
          </ul>
        )}
        {/* Sale price bold, original struck beside it -- the same stacking the
            package cards use. */}
        {rawPrice !== null && (
          <p className={styles.priceRow}>
            <span className={styles.salePrice}>
              {money(discounted != null ? discounted : rawPrice) || '$0'}
            </span>
            {discounted != null && (
              <span className={styles.wasPrice}>{money(rawPrice)}</span>
            )}
          </p>
        )}
      </div>

      <Button
        fullWidth
        type="button"
        variant="submit"
        classes={styles.cta}
        disabled={isAdding || !customer.cart || !backgrounds}
        onClick={addToCart}
      >
        {isAdding ? 'Adding…' : 'Add to Cart'}
      </Button>
    </div>
  );
};

export default SportsTeamPicture;
